import React, { useEffect, useState, useRef } from 'react';
import { UserIcon, PlusIcon, SearchIcon, XIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { addStudentToTeacher } from '../services/studentService';
import { getTeacherStudents } from '../utils/teacherUtils';
import { formatPhoneNumber, normalizePhoneNumber } from '../utils/phoneUtils';
import { toast } from 'react-hot-toast';
interface Student {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  instrument?: string;
}
interface StudentPickerProps {
  value: string;
  onChange: (studentId: string) => void;
  onAddStudent?: (student: Student) => void;
  students: Student[];
  darkMode?: boolean;
  required?: boolean;
  loading?: boolean;
}
export const StudentPicker: React.FC<StudentPickerProps> = ({
  value,
  onChange,
  onAddStudent,
  students,
  darkMode = false,
  required = false,
  loading = false
}) => {
  const {
    currentUser
  } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newStudent, setNewStudent] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    instrument: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<{
    firstName?: string;
    email?: string;
    phone?: string;
    duplicate?: string;
  }>({});
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);
  // Get selected student
  const selectedStudent = students.find(s => s.id === value);
  // Filter students based on search
  const filteredStudents = students.filter(student => student.name.toLowerCase().includes(search.toLowerCase()) || student.email && student.email.toLowerCase().includes(search.toLowerCase()));
  // Email validation (same as AddStudentModal)
  const validateEmail = (email: string): boolean => {
    if (!email.trim()) {
      setErrors(prev => ({
        ...prev,
        email: 'Email is required'
      }));
      return false;
    }
    if (email.includes(' ')) {
      setErrors(prev => ({
        ...prev,
        email: 'Email cannot contain spaces'
      }));
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrors(prev => ({
        ...prev,
        email: 'Please enter a valid email address (example: name@example.com)'
      }));
      return false;
    }
    setErrors(prev => ({
      ...prev,
      email: undefined
    }));
    return true;
  };
  // Phone validation - UPDATED: Optional
  const validatePhone = (phone: string): boolean => {
    // Phone is now optional - if empty, it's valid
    if (!phone.trim()) {
      setErrors(prev => ({
        ...prev,
        phone: undefined
      }));
      return true;
    }
    const digitsOnly = phone.replace(/[^\d]/g, '');
    const allowedCharsRegex = /^[\d\s\-\+\(\)]+$/;
    if (!allowedCharsRegex.test(phone)) {
      setErrors(prev => ({
        ...prev,
        phone: 'Phone number can only contain digits, spaces, +, -, ( and )'
      }));
      return false;
    }
    if (digitsOnly.length < 8 || digitsOnly.length > 15) {
      setErrors(prev => ({
        ...prev,
        phone: 'Please enter a valid phone number with country code'
      }));
      return false;
    }
    setErrors(prev => ({
      ...prev,
      phone: undefined
    }));
    return true;
  };
  // Duplicate check (same as AddStudentModal)
  const checkDuplicateStudent = async (firstName: string, lastName: string, email: string, phone: string): Promise<boolean> => {
    if (!currentUser?.uid) {
      return true;
    }
    try {
      const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
      const normalizedName = fullName.toLowerCase().trim();
      const normalizedEmail = email.toLowerCase().trim();
      const normalizedPhone = normalizePhoneNumber(phone);
      const existingStudents = await getTeacherStudents(currentUser.uid);
      // Check for duplicate NAME
      const duplicateName = existingStudents.find(s => s.name.toLowerCase().trim() === normalizedName);
      if (duplicateName) {
        setErrors(prev => ({
          ...prev,
          duplicate: `A student named "${fullName}" already exists. Please select the existing student.`
        }));
        return false;
      }
      // Check for duplicate EMAIL
      if (normalizedEmail) {
        const duplicateEmail = existingStudents.find(s => s.email && s.email.toLowerCase().trim() === normalizedEmail);
        if (duplicateEmail) {
          setErrors(prev => ({
            ...prev,
            duplicate: `A student with email "${email}" already exists (${duplicateEmail.name}).`
          }));
          return false;
        }
      }
      // Check for duplicate PHONE
      if (normalizedPhone && normalizedPhone.length >= 8) {
        const duplicatePhone = existingStudents.find(s => {
          if (!s.phone) return false;
          const existingNormalizedPhone = normalizePhoneNumber(s.phone);
          return existingNormalizedPhone === normalizedPhone;
        });
        if (duplicatePhone) {
          setErrors(prev => ({
            ...prev,
            duplicate: `A student with phone number "${phone}" already exists (${duplicatePhone.name}).`
          }));
          return false;
        }
      }
      setErrors(prev => ({
        ...prev,
        duplicate: undefined
      }));
      return true;
    } catch (error) {
      console.error('[StudentPicker] Error checking for duplicates:', error);
      return true;
    }
  };
  // Handle add new student - FIXED: Save to Firestore
  const handleAddStudent = async () => {
    if (!currentUser?.uid) {
      toast.error('You must be logged in to add a student');
      return;
    }
    // Clear previous errors
    setErrors({});
    // Validate required fields
    if (!newStudent.firstName.trim()) {
      setErrors(prev => ({
        ...prev,
        firstName: 'First name is required'
      }));
      return;
    }
    // Validate email and phone
    const isEmailValid = validateEmail(newStudent.email);
    const isPhoneValid = validatePhone(newStudent.phone);
    if (!isEmailValid || !isPhoneValid) {
      toast.error('Please fix the validation errors before submitting');
      return;
    }
    // Check for duplicates
    const isUnique = await checkDuplicateStudent(newStudent.firstName, newStudent.lastName, newStudent.email, newStudent.phone);
    if (!isUnique) {
      toast.error('Cannot create duplicate student');
      return;
    }
    setIsSaving(true);
    try {
      // Create complete student object (same as AddStudentModal)
      const studentId = `student-${Date.now()}`;
      const fullName = `${newStudent.firstName.trim()} ${newStudent.lastName.trim()}`.trim();
      const normalizedPhone = normalizePhoneNumber(newStudent.phone);
      const studentData = {
        studentId,
        name: fullName,
        firstName: newStudent.firstName.trim(),
        lastName: newStudent.lastName.trim(),
        email: newStudent.email.trim(),
        phone: normalizedPhone || '',
        instrument: newStudent.instrument.trim(),
        notes: '',
        profilePicture: '',
        status: 'active'
      };
      console.log('[StudentPicker] Creating student in Firestore:', studentData);
      // Save to Firestore (same as Students page)
      await addStudentToTeacher(currentUser.uid, studentData);
      console.log('[StudentPicker] Student saved to Firestore successfully');
      // Create student object for callback
      const studentToAdd: Student = {
        id: studentId,
        name: fullName,
        email: studentData.email,
        phone: studentData.phone,
        instrument: studentData.instrument
      };
      // Notify parent component
      if (onAddStudent) {
        onAddStudent(studentToAdd);
      }
      // Reset form
      setNewStudent({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        instrument: ''
      });
      setErrors({});
      setIsAddingNew(false);
      onChange(studentId);
      setIsOpen(false);
      toast.success('Student added successfully!');
    } catch (error) {
      console.error('[StudentPicker] Error adding student:', error);
      toast.error('Failed to add student. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };
  // Handle clear selection
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setSearch('');
  };
  return <div className="relative" ref={dropdownRef} data-id="element-10926">
      {/* Selected student display or search input */}
      <div className={`flex items-center justify-between w-full px-3 py-2 rounded-md border ${darkMode ? 'bg-navy-900 border-navy-700 text-white' : 'bg-white border-gray-300 text-gray-900'} cursor-pointer`} onClick={() => setIsOpen(!isOpen)} data-id="element-10927">
        <div className="flex items-center flex-grow" data-id="element-10928">
          <UserIcon className="h-4 w-4 text-[#3B82F6] mr-2 flex-shrink-0" data-id="element-10929" />
          {!isOpen ? <span className={selectedStudent ? 'text-sm' : `text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10930">
              {selectedStudent ? selectedStudent.name : 'Select a student'}
            </span> : <input ref={inputRef} type="text" value={search} onChange={e => setSearch(e.target.value)} onClick={e => e.stopPropagation()} placeholder="Search students..." className={`w-full border-none outline-none ${darkMode ? 'bg-navy-900 text-white' : 'bg-white text-gray-900'} placeholder:text-gray-400 focus:ring-0`} data-id="element-10931" />}
        </div>
        {/* Clear button when a student is selected */}
        {value && !isOpen && <button type="button" onClick={handleClear} className={`ml-2 p-0.5 rounded-full ${darkMode ? 'hover:bg-navy-700' : 'hover:bg-gray-100'}`} data-id="element-10932">
            <XIcon className="h-4 w-4 text-gray-400" data-id="element-10933" />
          </button>}
        {/* Search icon when dropdown is open */}
        {isOpen && <SearchIcon className="h-4 w-4 text-gray-400 ml-2" data-id="element-10934" />}
      </div>

      {/* Dropdown */}
      {isOpen && <div className={`absolute z-10 w-full mt-1 rounded-md shadow-lg ${darkMode ? 'bg-navy-800 border border-navy-700' : 'bg-white border border-gray-200'}`} data-id="element-10935">
          {/* Student list */}
          {!isAddingNew && <ul className={`max-h-60 overflow-auto py-1 text-sm ${darkMode ? 'text-white' : 'text-gray-700'}`} data-id="element-10936">
              {loading ? <li className="px-4 py-2 text-center" data-id="element-10937">Loading students...</li> : filteredStudents.length > 0 ? filteredStudents.map(student => <li key={student.id} className={`px-4 py-2 cursor-pointer flex items-center ${student.id === value ? darkMode ? 'bg-navy-700' : 'bg-gray-100' : darkMode ? 'hover:bg-navy-700' : 'hover:bg-gray-50'}`} onClick={() => {
          onChange(student.id);
          setIsOpen(false);
        }} data-id="element-10938">
                    <span className="flex-grow" data-id="element-10939">{student.name}</span>
                    {student.email && <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10940">
                        {student.email}
                      </span>}
                  </li>) : <li className="px-4 py-2 text-center" data-id="element-10941">No students found</li>}

              {/* Add new student option */}
              <li className={`px-4 py-2 cursor-pointer flex items-center ${darkMode ? 'hover:bg-navy-700 text-[#10B981]' : 'hover:bg-gray-50 text-[#10B981]'} border-t ${darkMode ? 'border-navy-700' : 'border-gray-100'}`} onClick={() => setIsAddingNew(true)} data-id="element-10942">
                <PlusIcon className="h-4 w-4 mr-2" data-id="element-10943" />
                <span data-id="element-10944">Add new student</span>
              </li>
            </ul>}

          {/* Add new student form - UPDATED with required fields */}
          {isAddingNew && <div className={`p-3 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-10945">
              <h4 className="font-medium mb-2 text-sm" data-id="element-10946">Add New Student</h4>

              {/* Duplicate warning */}
              {errors.duplicate && <div className={`mb-2 p-2 rounded text-xs ${darkMode ? 'bg-yellow-900/30 text-yellow-200' : 'bg-yellow-50 text-yellow-700'}`} data-id="element-10947">
                  {errors.duplicate}
                </div>}

              <div className="space-y-2" data-id="element-10948">
                {/* First Name - Required */}
                <div data-id="element-10949">
                  <input type="text" value={newStudent.firstName} onChange={e => setNewStudent({
              ...newStudent,
              firstName: e.target.value
            })} placeholder="First name *" className={`w-full px-3 py-2 text-sm rounded-md border ${errors.firstName ? 'border-red-500' : darkMode ? 'border-navy-700' : 'border-gray-300'} ${darkMode ? 'bg-navy-900 text-white placeholder-gray-400' : 'bg-white text-gray-900 placeholder-gray-500'} focus:border-deep-pink shadow-sm focus:ring-0 focus:ring-offset-0`} autoFocus data-id="element-10950" />
                  {errors.firstName && <p className="text-xs text-red-500 mt-0.5" data-id="element-10951">
                      {errors.firstName}
                    </p>}
                </div>

                {/* Last Name */}
                <div data-id="element-10952">
                  <input type="text" value={newStudent.lastName} onChange={e => setNewStudent({
              ...newStudent,
              lastName: e.target.value
            })} placeholder="Last name" className={`w-full px-3 py-2 text-sm rounded-md border ${darkMode ? 'bg-navy-900 border-navy-700 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'} focus:border-deep-pink shadow-sm focus:ring-0 focus:ring-offset-0`} data-id="element-10953" />
                </div>

                {/* Email - Required */}
                <div data-id="element-10954">
                  <input type="email" value={newStudent.email} onChange={e => setNewStudent({
              ...newStudent,
              email: e.target.value
            })} placeholder="Email *" className={`w-full px-3 py-2 text-sm rounded-md border ${errors.email ? 'border-red-500' : darkMode ? 'border-navy-700' : 'border-gray-300'} ${darkMode ? 'bg-navy-900 text-white placeholder-gray-400' : 'bg-white text-gray-900 placeholder-gray-500'} focus:border-deep-pink shadow-sm focus:ring-0 focus:ring-offset-0`} data-id="element-10955" />
                  {errors.email && <p className="text-xs text-red-500 mt-0.5" data-id="element-10956">
                      {errors.email}
                    </p>}
                </div>

                {/* Phone - Optional */}
                <div data-id="element-10957">
                  <input type="tel" value={newStudent.phone} onChange={e => setNewStudent({
              ...newStudent,
              phone: e.target.value
            })} placeholder="Phone (optional, e.g., +1 310 555 7890)" className={`w-full px-3 py-2 text-sm rounded-md border ${errors.phone ? 'border-red-500' : darkMode ? 'border-navy-700' : 'border-gray-300'} ${darkMode ? 'bg-navy-900 text-white placeholder-gray-400' : 'bg-white text-gray-900 placeholder-gray-500'} focus:border-deep-pink shadow-sm focus:ring-0 focus:ring-offset-0`} data-id="element-10958" />
                  {errors.phone && <p className="text-xs text-red-500 mt-0.5" data-id="element-10959">
                      {errors.phone}
                    </p>}
                </div>

                {/* Instrument - Optional */}
                <div data-id="element-10960">
                  <input type="text" value={newStudent.instrument} onChange={e => setNewStudent({
              ...newStudent,
              instrument: e.target.value
            })} placeholder="Instrument (optional)" className={`w-full px-3 py-2 text-sm rounded-md border ${darkMode ? 'bg-navy-900 border-navy-700 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'} focus:border-deep-pink shadow-sm focus:ring-0 focus:ring-offset-0`} data-id="element-10961" />
                </div>

                <div className="flex justify-end space-x-2 pt-1" data-id="element-10962">
                  <button type="button" onClick={() => {
              setIsAddingNew(false);
              setNewStudent({
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                instrument: ''
              });
              setErrors({});
            }} disabled={isSaving} className={`px-3 py-1.5 text-xs rounded ${darkMode ? 'bg-navy-700 text-white hover:bg-navy-600' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'} ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-10963">
                    Cancel
                  </button>
                  <button type="button" onClick={handleAddStudent} disabled={!newStudent.firstName.trim() || isSaving} className={`px-3 py-1.5 text-xs rounded border-2 border-[#10B981] text-[#10B981] bg-transparent hover:bg-[#10B981]/10 transition-colors ${!newStudent.firstName.trim() || isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-10964">
                    {isSaving ? 'Adding...' : 'Add Student'}
                  </button>
                </div>
              </div>
            </div>}
        </div>}
    </div>;
};